import { Component, computed, effect, input, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { InputComponent, TypographyComponent } from '@purolator/ui-kit-angular';
import { TranslatePipe } from '../../../../core/i18n';
import { AlertToast } from '../../../../shared/components/alert-toast/alert-toast';
import { customerPieceFieldHasError } from '../../customer-piece-field';
import { DELIVERY_INSTRUCTIONS_MAX_LENGTH } from '../../package-types.config';
import { DELIVERY_INSTRUCTIONS_VALIDATION_ERROR_KEYS } from '../../validators/delivery-instructions.validators';

/**
 * Optional "Special Instructions For Delivery" field printed in the shipment label Notes field
 * (max length + restricted-character validation).
 */
@Component({
  selector: 'app-delivery-instructions-section',
  imports: [ReactiveFormsModule, AlertToast, InputComponent, TypographyComponent, TranslatePipe],
  templateUrl: './delivery-instructions-section.html',
  styleUrl: './delivery-instructions-section.scss',
})
export class DeliveryInstructionsSection {
  readonly control = input.required<FormControl<string>>();
  /** When true, shows the field error and restricted-characters banner after Save. */
  readonly showValidationErrors = input(false);

  protected readonly maxLength = DELIVERY_INSTRUCTIONS_MAX_LENGTH;

  /**
   * Bumps when the value changes or the field is blurred so error state
   * re-reads FormControl validity (controls are not signals).
   */
  private readonly validationRevision = signal(0);

  constructor() {
    effect((onCleanup) => {
      const control = this.control();
      this.showValidationErrors();
      this.validationRevision.update((value) => value + 1);

      const subscription = control.valueChanges.subscribe(() => {
        this.validationRevision.update((value) => value + 1);
      });

      onCleanup(() => subscription.unsubscribe());
    });
  }

  /**
   * Whether the field should show an error state (invalid after blur or Save).
   */
  protected readonly hasFieldError = computed(() => {
    this.validationRevision();
    return customerPieceFieldHasError(this.control(), this.showValidationErrors());
  });

  /**
   * Whether the restricted-characters banner should be shown.
   */
  protected readonly hasRestrictedCharactersError = computed(
    () =>
      this.hasFieldError() &&
      this.control().hasError(DELIVERY_INSTRUCTIONS_VALIDATION_ERROR_KEYS.restrictedCharacters),
  );

  /**
   * Marks the field touched on blur so inline validation can display without Save.
   */
  protected onFieldBlur(): void {
    this.control().markAsTouched();
    this.validationRevision.update((value) => value + 1);
  }
}
